import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProductsShowcase from '@/components/ProductsShowcase';
import { categories } from '@/data/categories';
import { getPillarArticles } from '@/data/pillarArticles';

const commercialCards = [
  {
    key: 'review',
    icon: 'ri-star-line',
    img: 'https://readdy.ai/api/search-image?query=Five%20gold%20stars%20rating%20graphic%20above%20a%20product%20catalog%20on%20minimal%20cream%20background%2C%20review%20concept%2C%20soft%20lighting%2C%20clean%20editorial%20photography&width=600&height=400&seq=commercial-review-01&orientation=landscape',
  },
  {
    key: 'shippingCost',
    icon: 'ri-truck-line',
    img: 'https://readdy.ai/api/search-image?query=Cardboard%20parcel%20box%20with%20shipping%20label%20and%20tape%20next%20to%20measuring%20tape%20on%20light%20background%2C%20shipping%20cost%20concept%2C%20soft%20light%2C%20minimal%20photography&width=600&height=400&seq=commercial-shipping-01&orientation=landscape',
  },
  {
    key: 'coupon',
    icon: 'ri-coupon-line',
    img: 'https://readdy.ai/api/search-image?query=Discount%20coupon%20ticket%20with%20scissors%20and%20percent%20sign%20on%20soft%20pink%20background%2C%20promotion%20concept%2C%20bright%20clean%20minimal%20photography&width=600&height=400&seq=commercial-coupon-01&orientation=landscape',
  },
  {
    key: 'discountCode',
    icon: 'ri-price-tag-3-line',
    img: 'https://readdy.ai/api/search-image?query=Promo%20code%20card%20with%20barcode%20next%20to%20shopping%20bags%20on%20light%20blue%20grey%20background%2C%20discount%20concept%2C%20soft%20lighting%2C%20minimal%20product%20photography&width=600&height=400&seq=commercial-discount-01&orientation=landscape',
  },
  {
    key: 'warehouse',
    icon: 'ri-store-3-line',
    img: 'https://readdy.ai/api/search-image?query=Neat%20rows%20of%20cardboard%20boxes%20on%20warehouse%20shelves%20with%20warm%20light%2C%20logistics%20storage%20concept%2C%20clean%20industrial%20photography&width=600&height=400&seq=commercial-warehouse-01&orientation=landscape',
  },
  {
    key: 'legit',
    icon: 'ri-shield-check-line',
    img: 'https://readdy.ai/api/search-image?query=Green%20shield%20with%20checkmark%20and%20lock%20icon%20on%20clean%20white%20background%2C%20security%20trust%20concept%2C%20soft%20light%2C%20minimal%20flat%20illustration&width=600&height=400&seq=commercial-legit-01&orientation=landscape',
  },
];

const faqItems = [
  { q: 'faq1Q', a: 'faq1A' },
  { q: 'faq2Q', a: 'faq2A' },
  { q: 'faq3Q', a: 'faq3A' },
  { q: 'faq4Q', a: 'faq4A' },
  { q: 'faq5Q', a: 'faq5A' },
  { q: 'faq6Q', a: 'faq6A' },
  { q: 'faq7Q', a: 'faq7A' },
  { q: 'faq8Q', a: 'faq8A' },
  { q: 'faq9Q', a: 'faq9A' },
  { q: 'faq10Q', a: 'faq10A' },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const pillarArticles = getPillarArticles(i18n.language);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Announcement Bar */}
      <div className="bg-gray-900 text-white text-xs text-center py-2 px-4 mt-16">
        {t('announcement')}
      </div>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Colorful%20streetwear%20clothing%20rack%20with%20sneakers%20and%20caps%20in%20a%20bright%20minimal%20boutique%20store%2C%20vivid%20warm%20colors%2C%20soft%20natural%20light%2C%20editorial%20retail%20photography&width=1600&height=900&seq=hero-bg-02&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {t('heroH1')}
              </h1>
              <p className="text-lg text-gray-200 mb-8 leading-relaxed">
                {t('heroSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  {t('heroCtaPrimary')}
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30 whitespace-nowrap"
                >
                  {t('heroCtaSecondary')}
                </a>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
                >
                  {t('cssbuySpreadsheet')}
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
                >
                  {t('buyItNow')}
                </a>
              </div>
              <p className="mt-6 text-sm text-gray-300">
                {t('heroTrust')}
              </p>
              <p className="mt-2 text-xs text-gray-400">
                {t('heroDisclaimer')}
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="aspect-video rounded-xl overflow-hidden mb-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stack%20of%20colorful%20sneaker%20boxes%20and%20folded%20streetwear%20on%20minimal%20shelves%2C%20warm%20ambient%20boutique%20lighting%2C%20modern%20retail%20editorial%20photography&width=800&height=450&seq=hero-card-02&orientation=landscape&nocache=true"
                    alt="CSSBuy Spreadsheet Products"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{t('productCategoriesTitle')}</h3>
                <p className="text-gray-300 text-sm">{t('productCategoriesDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section id="what-is" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">{t('whatIsTitle')}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2 mb-4">
                {t('whatIsH2')}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t('whatIsDesc')}
              </p>
              <p className="text-amber-700 bg-amber-50 p-4 rounded-lg text-sm leading-relaxed mb-6">
                {t('whatIsWarning')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center text-green-600 font-semibold hover:text-green-700 transition-colors"
              >
                {t('whatIsCta')}
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Tablet%20displaying%20spreadsheet%20product%20list%20surrounded%20by%20sneakers%20and%20shopping%20bags%20on%20warm%20wooden%20desk%2C%20soft%20natural%20light%2C%20editorial%20productivity%20photography&width=800&height=600&seq=whatis-img-02&orientation=landscape&nocache=true"
                alt="CSSBuy Spreadsheet Guide"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Use / Features */}
      <section id="why-use" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">{t('whyUseTitle')}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">{t('whyUseH2')}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { title: 'whyFindTitle', desc: 'whyFindDesc', icon: 'ri-search-line' },
              { title: 'whyCompareTitle', desc: 'whyCompareDesc', icon: 'ri-bar-chart-box-line' },
              { title: 'whyShippingTitle', desc: 'whyShippingDesc', icon: 'ri-truck-line' },
              { title: 'whyQCTitle', desc: 'whyQCDesc', icon: 'ri-camera-line' },
              { title: 'whySafetyTitle', desc: 'whySafetyDesc', icon: 'ri-shield-check-line' },
            ].map((feature) => (
              <div key={feature.title} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-green-600 text-xl`} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{t(feature.title)}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{t(feature.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-500 font-semibold text-sm uppercase tracking-wide">{t('categoriesTitle')}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">{t('productCategoriesTitle')}</h2>
            <p className="text-gray-600 mt-2">{t('productCategoriesDesc')}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative rounded-xl overflow-hidden aspect-square bg-gray-100 hover:shadow-lg transition-all"
              >
                <img src={cat.img} alt={t(cat.key)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <span className="text-white font-medium text-sm">{t(cat.key)}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Products Showcase */}
      <ProductsShowcase />

      {/* Commercial SEO Cards */}
      <section id="commercial" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">{t('commercialTitle')}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">CSSBuy Resources</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {commercialCards.map((card) => (
              <div key={card.key} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <div className="aspect-[3/2] overflow-hidden">
                  <img src={card.img} alt={t(`${card.key}Title`)} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center mb-3">
                    <i className={`${card.icon} text-orange-500 text-lg`} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t(`${card.key}Title`)}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">{t(`${card.key}Desc`)}</p>
                  <Link to="/article" className="text-green-600 text-sm font-medium hover:text-green-700 transition-colors">
                    {t(`${card.key}Cta`)}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar Guides Section */}
      <section id="pillar-guides" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">{t('pillarSectionLabel')}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">{t('pillarSectionTitle')}</h2>
            <p className="text-gray-600 mt-2">{t('pillarSectionDesc')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {pillarArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:border-green-200 transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-2 text-base leading-snug group-hover:text-green-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{article.excerpt}</p>
                  <span className="inline-flex items-center text-green-600 text-sm font-medium mt-3 group-hover:gap-2 transition-all">
                    {t('readMore')}
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section id="comparison" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">{t('comparisonTitle')}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">{t('comparisonH2')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-4 px-4 font-semibold text-gray-900">{t('comparisonFeature')}</th>
                  <th className="py-4 px-4 font-semibold text-green-600 text-center">{t('comparisonCssbuy')}</th>
                  <th className="py-4 px-4 font-semibold text-gray-500 text-center">{t('comparisonAlternative')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'comparisonProductResearch',
                  'comparisonSpreadsheetResources',
                  'comparisonQCInfo',
                  'comparisonShippingGuide',
                  'comparisonWarehouseGuide',
                  'comparisonSafetyGuide',
                ].map((row) => (
                  <tr key={row} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-4 text-gray-700">{t(row)}</td>
                    <td className="py-4 px-4 text-center text-green-600 font-bold">{t('comparisonYes')}</td>
                    <td className="py-4 px-4 text-center text-green-600 font-bold">{t('comparisonYes')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wide">{t('faqTitle')}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">{t('faqH2')}</h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium text-gray-900 text-sm">{t(faq.q)}</span>
                  <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">
                    {t(faq.a)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gray-900 relative">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20flat%20lay%20of%20colorful%20sneakers%20caps%20and%20hoodies%20arranged%20in%20grid%20pattern%20on%20warm%20neutral%20background%2C%20editorial%20streetwear%20photography&width=1600&height=600&seq=cta-bg-02&orientation=landscape&nocache=true"
            alt="CSSBuy"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('finalCtaTitle')}</h2>
          <p className="text-gray-300 mb-8">{t('finalCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
            >
              {t('finalCtaPrimary')}
            </a>
            <Link
              to="/article"
              className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30 whitespace-nowrap"
            >
              {t('finalCtaSecondary')}
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
            >
              {t('cssbuySpreadsheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
            >
              {t('buyItNow')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}